// source: template-project@7007a338a apps/backoffice/src/pages/onboarding-v2/TasksPanel.tsx (card, header, row geometry, BADGE map), TRACKING ONLY (plan P3-2: no row is clickable)
import { Badge, type BadgeProps } from '@/components/ui/shadcn/badge';
import { Card } from '@/components/ui/shadcn/card';
import { cn } from '@/lib/utils';

import { TASKS } from '../copy';
import { tasksHeadline, type Task, type TaskStatus } from '../state/deriveTasks';
import { Elapsed } from './Elapsed';

/** TP's map, plus (NEW) partial = warning without the pulse and unavailable = error. The pulse is guarded for reduced motion (TP's is not). */
export const TASK_BADGE: Record<TaskStatus, { label: string; intent: NonNullable<BadgeProps['intent']> }> = {
  running: { label: TASKS.badge.running, intent: 'warning' },
  'needs-you': { label: TASKS.badge.needsYou, intent: 'warning' },
  'up-next': { label: TASKS.badge.upNext, intent: 'neutral' },
  done: { label: TASKS.badge.done, intent: 'success' },
  skipped: { label: TASKS.badge.skipped, intent: 'neutral' },
  partial: { label: TASKS.badge.partial, intent: 'warning' },
  unavailable: { label: TASKS.badge.unavailable, intent: 'error' },
};

export interface TasksPanelProps {
  tasks: readonly Task[];
}

/**
 * The Tasks panel beside the conversation: every background task of the setup, in the order it runs, each with
 * where it stands. Tracking only: a row is a plain `li` (no button, no hover, no chevron, no focus stop), because
 * opening a task needs artifacts that come in a later phase. The list is one polite live region; the running
 * clock is its own leaf (it ticks without re-rendering the panel) and is hidden from assistive tech so a screen
 * reader is not told the time every second.
 */
export function TasksPanel({ tasks }: TasksPanelProps) {
  return (
    <Card role="region" aria-label={TASKS.panelLabel} data-testid="tasks-panel" className="flex flex-col">
      <header className="flex items-baseline justify-between gap-2 border-b border-ds-hairline px-4 py-3">
        <h2 className="text-heading-sm text-foreground">{TASKS.panelLabel}</h2>
        <span data-testid="tasks-headline" className="text-label text-ds-text-2">
          {tasksHeadline(tasks)}
        </span>
      </header>
      <ul className="flex flex-col p-2" aria-live="polite">
        {tasks.map((t) => {
          const Icon = t.icon;
          const badge = TASK_BADGE[t.status];
          const quiet = t.status === 'up-next' || t.status === 'skipped';
          return (
            <li key={t.id} data-task-id={t.id} data-status={t.status}>
              <div className="flex items-center gap-3 rounded-md px-2 py-2">
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-8 shrink-0 items-center justify-center rounded-md transition-colors',
                    t.status === 'done' ? 'bg-ds-badge-success-bg text-ds-badge-success-text' : 'bg-ds-ink-a05 text-ds-text-2',
                  )}
                >
                  <Icon className="size-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className={cn('block text-tab', quiet ? 'text-ds-text-2' : 'text-foreground')}>{t.title}</span>
                  <span
                    title={t.detail}
                    className={cn('block truncate text-label text-ds-text-2', t.status === 'running' && 'onb-shimmer')}
                  >
                    {t.detail}
                    {t.status === 'running' && t.clock ? (
                      <span aria-hidden="true">
                        {' · '}
                        <Elapsed startedAt={t.clock.startedAt} frozenAt={t.clock.frozenAt} testId="tasks-elapsed" />
                      </span>
                    ) : null}
                  </span>
                </span>
                <Badge
                  intent={badge.intent}
                  dot
                  className={cn('shrink-0', t.status === 'running' && '[&>span:first-child]:motion-safe:animate-pulse')}
                >
                  {badge.label}
                </Badge>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
